import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_LIST, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import PrivateVideoPlayer from "@/components/video/PrivateVideoPlayer";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const BG = "#F4EBD8";
const TEXT = "#2A1D14";
const MUTED = "#5B4326";
const EYEBROW = "#8A6A46";
const CROP_ASPECT = "4/5";

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    caption: raw.caption || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

function normalizeVideoAsset(raw) {
  if (!raw?.media_id) return null;
  return {
    media_id: String(raw.media_id),
    media_type: raw.media_type || "video",
    mime_type: raw.mime_type || "video/mp4",
    size_bytes: raw.size_bytes ?? null,
    original_filename: raw.original_filename || null,
    safe_metadata: raw.safe_metadata || {},
    access_scope: raw.access_scope || "owner_private",
    entitlement_product_id: raw.entitlement_product_id || null,
    content_path: raw.content_path || `/api/private-media/assets/${raw.media_id}/content`,
  };
}

function deriveInitial(content) {
  const c = content || {};
  const v = c.extra?.video_master && typeof c.extra.video_master === "object" ? c.extra.video_master : {};
  return {
    eyebrow: v.eyebrow ?? "Видео",
    title: v.title ?? c.headline ?? "",
    description: v.description ?? c.lead ?? "",
    mode: v.mode === "lesson" ? "lesson" : "short",
    asset: normalizeVideoAsset(v.asset),
    poster: normalizeImage(v.poster),
  };
}

function durationLabel(milliseconds) {
  const seconds = Math.round(Number(milliseconds || 0) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function VideoLibraryPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    api.get(MEDIA_LIST)
      .then(({ data }) => {
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        setItems(records.filter((m) => String(m.file_type || m.media_type || "").toLowerCase() === "video"));
      })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((m) => String(m.original_filename || m.title || m.filename || "").toLowerCase().includes(q))
    : items;

  const choose = async (media) => {
    try {
      const { data } = await api.get(`/private-media/assets/${media.id}`);
      onPick(normalizeVideoAsset(data));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-center justify-between border-b border-espresso/10 px-5 py-4">
          <div className="font-serif text-lg text-espresso">Видео из Медиатеки</div>
          <button type="button" onClick={onClose} className="text-sm text-espresso/60">Закрыть</button>
        </div>
        <div className="px-5 pt-3">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по имени файла"
            className="w-full rounded-lg border border-espresso/20 bg-white px-3 py-2 text-sm" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="py-8 text-center text-sm text-espresso/60">Загрузка…</div>}
          {!loading && filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-espresso/60">Видео пока не найдено.</div>
          )}
          <div className="space-y-2">
            {filtered.map((m) => (
              <button key={m.id} type="button" onClick={() => choose(m)}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-espresso/15 bg-white px-4 py-3 text-left hover:border-espresso/35">
                <div className="min-w-0">
                  <div className="truncate font-serif text-base text-espresso">{m.original_filename || m.title || "Видео"}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-espresso/45">Private video</div>
                </div>
                <div className="shrink-0 text-xs text-espresso/50">Выбрать</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function EditorialVideoV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);
  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [description, setDescription] = useState(initial.description);
  const [mode, setMode] = useState(initial.mode);
  const [asset, setAsset] = useState(initial.asset);
  const [poster, setPoster] = useState(initial.poster);
  const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
  const [posterLibraryOpen, setPosterLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const videoInput = useRef(null);
  const posterInput = useRef(null);

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setDescription(next.description);
    setMode(next.mode);
    setAsset(next.asset);
    setPoster(next.poster);
    setVideoLibraryOpen(false);
    setPosterLibraryOpen(false);
    setCropOpen(false);
    setSaveState("idle");
  }, [block.id]);
  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("media_type", "video");
      form.append("source_product", "editorial");
      form.append("purpose", "editorial_video");
      if (volume?.product_id) {
        form.append("access_scope", "product_entitled");
        form.append("entitlement_product_id", volume.product_id);
      } else {
        form.append("access_scope", "owner_private");
      }
      const { data } = await api.post("/private-media/assets", form);
      setAsset(normalizeVideoAsset(data));
      toast.success("Видео загружено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingVideo(false);
    }
  };

  const preparePickedVideo = async (picked) => {
    if (!picked) return;
    if (!volume?.product_id) {
      setAsset(picked);
      setVideoLibraryOpen(false);
      return;
    }
    if (picked.access_scope === "product_entitled" && picked.entitlement_product_id === volume.product_id) {
      setAsset(picked);
      setVideoLibraryOpen(false);
      return;
    }
    try {
      const { data: blob } = await api.get(`/private-media/assets/${picked.media_id}/content`, { responseType: "blob" });
      const file = new File([blob], picked.original_filename || "video.mp4", { type: picked.mime_type || "video/mp4" });
      const form = new FormData();
      form.append("file", file);
      form.append("media_type", "video");
      form.append("source_product", "editorial");
      form.append("purpose", "editorial_video");
      form.append("access_scope", "product_entitled");
      form.append("entitlement_product_id", volume.product_id);
      const { data } = await api.post("/private-media/assets", form);
      setAsset(normalizeVideoAsset(data));
      setVideoLibraryOpen(false);
      toast.success("Видео подготовлено для доступа по продукту");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pickPoster = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setPoster(normalizeImage({ media_id: media.id, url }));
    setPosterLibraryOpen(false);
  };

  const uploadPoster = async (file) => {
    if (!file) return;
    setUploadingPoster(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setPoster(normalizeImage({ media_id: data.id, url }));
      toast.success("Обложка загружена");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingPoster(false);
    }
  };

  const save = async () => {
    if (volume?.product_id && asset && !(asset.access_scope === "product_entitled" && asset.entitlement_product_id === volume.product_id)) {
      toast.error("Это видео ещё не подготовлено для доступа выбранного продукта.");
      return;
    }
    setSaveState("saving");
    try {
      const payload = {
        eyebrow,
        title,
        description,
        mode,
        asset: asset ? { ...asset } : null,
        poster: poster ? { ...poster } : null,
      };
      const prevExtra = c.extra && typeof c.extra === "object" ? c.extra : {};
      const mediaIds = poster?.media_id ? [poster.media_id] : [];
      const nextContent = {
        ...c,
        eyebrow,
        headline: title,
        lead: description,
        media_ids: mediaIds,
        extra: { ...prevExtra, video_master: payload },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      onSaved?.(
        { ...block, content: nextContent },
        poster?.media_id && poster?.url ? [{ id: poster.media_id, url: poster.url }] : []
      );
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const metadata = asset?.safe_metadata || {};
  const saveLabel = { idle:"", saving:"Сохранение…", saved:"Сохранено", error:"Ошибка сохранения" }[saveState];

  return (
    <div onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between border-b border-espresso/10 px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Видео</div>
            <div className="mt-0.5 font-serif text-xl text-espresso">{title || "Видео"}</div>
          </div>
          {saveLabel && <span className="text-[11px] uppercase tracking-widest text-espresso/50">{saveLabel}</span>}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          <div className="space-y-3">
            <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} placeholder="Метка раздела"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Название видео"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 font-serif text-lg" />
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              placeholder="Короткое описание · необязательно"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="mb-2 text-[11px] text-espresso/60">Тип видео</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode("short")}
                className={mode === "short" ? "rounded-xl bg-espresso px-3 py-2 text-xs text-cream" : "rounded-xl border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"}>Короткое видео</button>
              <button type="button" onClick={() => setMode("lesson")}
                className={mode === "lesson" ? "rounded-xl bg-espresso px-3 py-2 text-xs text-cream" : "rounded-xl border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"}>Видеоурок</button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-[11px] text-espresso/60">Видео</div>
            {asset ? (
              <div className="rounded-2xl border border-espresso/15 bg-white/65 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-base text-espresso">{asset.original_filename || "Приватное видео"}</div>
                    <div className="mt-1 text-xs text-espresso/55">
                      {durationLabel(metadata.duration_ms)}
                      {metadata.width && metadata.height ? ` · ${metadata.width}×${metadata.height}` : ""}
                      {metadata.aspect_ratio ? ` · ${metadata.aspect_ratio}` : ""}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-espresso/45">
                      {asset.access_scope === "product_entitled" ? "Доступ по продукту" : "Только владелец"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => videoInput.current?.click()} disabled={uploadingVideo}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">
                      {uploadingVideo ? "Загрузка…" : "Заменить видео"}
                    </button>
                    <button type="button" onClick={() => setVideoLibraryOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Из Медиатеки</button>
                    <button type="button" onClick={() => setAsset(null)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Удалить</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => videoInput.current?.click()} disabled={uploadingVideo}
                  className="rounded-lg bg-espresso px-4 py-2 text-xs text-cream disabled:opacity-50">
                  {uploadingVideo ? "Загрузка…" : "Загрузить видео"}
                </button>
                <button type="button" onClick={() => setVideoLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-4 py-2 text-xs">Из Медиатеки</button>
              </div>
            )}
            <input ref={videoInput} type="file" accept="video/mp4" className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; uploadVideo(file); }} />
            <p className="mt-2 text-xs text-espresso/45">MP4 · до 30 минут на текущем Private Video foundation. Хранение и доставка идут через общий provider-neutral video pipeline.</p>
          </div>

          <div>
            <div className="mb-2 text-[11px] text-espresso/60">Обложка видео · необязательно</div>
            {poster?.url ? (
              <div className="flex items-start gap-3">
                <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
                  <img src={resolveMediaUrl(poster.url)} alt={poster.alt || ""} className="h-full w-full" style={editorialImageStyle(poster)} />
                </div>
                <div className="flex-1 space-y-2">
                  <input value={poster.alt} onChange={(e) => setPoster((img) => img ? { ...img, alt:e.target.value } : img)}
                    placeholder="Описание изображения (alt)" className="w-full rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                  <input value={poster.caption} onChange={(e) => setPoster((img) => img ? { ...img, caption:e.target.value } : img)}
                    placeholder="Подпись · необязательно" className="w-full rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => posterInput.current?.click()} disabled={uploadingPoster}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">{uploadingPoster?"Загрузка…":"Заменить фото"}</button>
                    <button type="button" onClick={() => setPosterLibraryOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Из Медиатеки</button>
                    <button type="button" onClick={() => setCropOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Настроить кадр</button>
                    <button type="button" onClick={() => setPoster(null)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Удалить фото</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button type="button" onClick={() => posterInput.current?.click()} disabled={uploadingPoster}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs">{uploadingPoster?"Загрузка…":"Добавить фото"}</button>
                <button type="button" onClick={() => setPosterLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs">Из Медиатеки</button>
              </div>
            )}
            <input ref={posterInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const file=e.target.files?.[0]; e.target.value=""; uploadPoster(file); }} />
          </div>

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")} className={previewDevice==="desktop"?"rounded-full bg-espresso px-2.5 py-1 text-cream":"rounded-full px-2.5 py-1"}>Desktop</button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")} className={previewDevice==="mobile"?"rounded-full bg-espresso px-2.5 py-1 text-cream":"rounded-full px-2.5 py-1"}>Mobile</button>
                </div>
              </div>
              <div className={previewDevice==="mobile"?"mx-auto max-w-[300px] p-5":"mx-auto max-w-[900px] p-7"} style={{backgroundColor:BG}}>
                <div className="text-[10px] uppercase tracking-[0.28em]" style={{color:EYEBROW}}>{eyebrow || "Видео"}</div>
                {title && <div className="mt-3 font-serif text-[30px] leading-[1.1]" style={{color:TEXT}}>{title}</div>}
                {description && <div className="mt-3 font-serif text-[15px] italic leading-[1.6]" style={{color:MUTED}}>{description}</div>}
                {asset ? (
                  <div className="mt-6"><PrivateVideoPlayer asset={asset} /></div>
                ) : poster?.url ? (
                  <div className="relative mt-6 aspect-video overflow-hidden bg-[#E7DDCC]">
                    <img src={resolveMediaUrl(poster.url)} alt={poster.alt || ""} className="h-full w-full" style={editorialImageStyle(poster)} />
                    <div className="absolute inset-0 flex items-center justify-center"><div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-xl text-white">▶</div></div>
                  </div>
                ) : null}
                {poster?.caption && <div className="mt-2 font-serif text-[12px] italic" style={{color:MUTED}}>{poster.caption}</div>}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose} className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm">Закрыть</button>
          <button type="button" onClick={() => setShowPreview((v)=>!v)} className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm">{showPreview?"Скрыть предпросмотр":"Предпросмотр разворота"}</button>
          <button type="button" onClick={save} disabled={saveState==="saving"} className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50">Сохранить изменения</button>
        </div>
      </div>

      <VideoLibraryPicker open={videoLibraryOpen} onClose={() => setVideoLibraryOpen(false)}
        onPick={preparePickedVideo} />
      <MediaLibraryPickerV2 open={posterLibraryOpen} onClose={() => setPosterLibraryOpen(false)} onPick={pickPoster} />
      <EditorialCropV2
        open={cropOpen}
        url={resolveMediaUrl(poster?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel="Обложка видео · 4:5"
        focalX={poster?.focal_x ?? 0.5}
        focalY={poster?.focal_y ?? 0.5}
        zoom={poster?.zoom ?? 1}
        onSave={(values) => { setPoster((img)=>img?{...img,...values}:img); setCropOpen(false); }}
        onClose={() => setCropOpen(false)}
      />
    </div>
  );
}
